// @owners { team: patients-team }
import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import { useSyncExternalStore } from 'react';
import { nextAvailableDatesQueryKeys } from '~shared/features/next-available-date/queries/nextAvailableDatesQueryKeys';
import { subscribeNextAvailableDatesQueryCache } from '~shared/features/next-available-date/queries/subscribeNextAvailableDatesQueryCache';

/** Latest `dataUpdatedAt` / `errorUpdatedAt` among all NAD-scoped queries (ms since epoch). */
export const getLastNextAvailableDatesFetchCompletedMsFromQueryClient = (queryClient: QueryClient): number => {
  let max = 0;
  for (const query of queryClient.getQueryCache().findAll({ queryKey: nextAvailableDatesQueryKeys.all })) {
    const { dataUpdatedAt, errorUpdatedAt } = query.state;
    max = Math.max(max, dataUpdatedAt, errorUpdatedAt);
  }
  return max;
};

/**
 * Replaces Redux `nextAvailableDates.lastRequestAttemptedAt` for analytics / cart hooks: last time any NAD query in this
 * scope finished (success or error).
 */
export const useLastNextAvailableDatesFetchCompletedAt = (): number => {
  const queryClient = useQueryClient();
  return useSyncExternalStore(
    (onStoreChange) => subscribeNextAvailableDatesQueryCache(queryClient, onStoreChange),
    () => getLastNextAvailableDatesFetchCompletedMsFromQueryClient(queryClient),
    () => 0,
  );
};
